import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { LoadingButton } from '@/components/LoadingButton'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'
import { useCreateOrganization } from '@/api/auth/organizations/organizations'
import { getGetMeOrganizationsQueryKey } from '@/api/auth/self/self'
import { useUpdateOrgSettings } from '@/api/control/control-plane/control-plane'
import { useError } from '@/contexts/ErrorContext'
import { useLanguage } from '@/contexts/LanguageContext'
import { getAvailableRegions, getRegionLabel } from '@/constants/regions'
import { CountryCombobox } from './CountryCombobox'

interface OrganizationFormProps {
  /** Called after org is successfully created */
  onSuccess: () => void;
  /** Called when user clicks cancel (optional - hides cancel button if not provided) */
  onCancel?: () => void;
  /** Label for the submit button */
  submitLabel?: string;
  /** Label for the cancel button */
  cancelLabel?: string;
  /** Whether the form is disabled */
  disabled?: boolean;
}

/**
 * Reusable form component for creating a new organization.
 * Used by both NewOrganizationDialog and OrganizationStep.
 */
export function OrganizationForm({
  onSuccess,
  onCancel,
  submitLabel,
  cancelLabel,
  disabled = false,
}: OrganizationFormProps) {
  const queryClient = useQueryClient();
  const { handleApiError } = useError();
  const { t } = useLanguage();
  const [organizationName, setOrganizationName] = useState('')
  const [selectedCountry, setSelectedCountry] = useState('')
  const [selectedRegion, setSelectedRegion] = useState<string>('none')
  const [isCreating, setIsCreating] = useState(false)

  const availableRegions = getAvailableRegions();
  const createOrganization = useCreateOrganization();
  const updateOrgSettings = useUpdateOrgSettings();

  const isFormDisabled = disabled || isCreating;
  const canSubmit = organizationName.trim() && selectedCountry && !isFormDisabled;

  /**
   * Handle form submission - create org with billing country and optionally set default region
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return;

    setIsCreating(true);
    try {
      // Create the organization with billing country
      const response = await createOrganization.mutateAsync({
        data: {
          name: organizationName.trim(),
          billingCountry: selectedCountry,
        }
      });

      // If a region was selected, update org settings with the default region
      if (selectedRegion !== 'none' && response.data.id) {
        await updateOrgSettings.mutateAsync({
          orgId: response.data.id,
          data: { defaultRegion: selectedRegion }
        });
      }

      toast.success(t.dialogs.newOrganization.organizationCreatedSuccessfully);
      void queryClient.invalidateQueries({ queryKey: getGetMeOrganizationsQueryKey() });
      onSuccess();
    } catch (error: unknown) {
      handleApiError(error, { context: 'OrganizationForm.createOrganization' });
    } finally {
      setIsCreating(false);
    }
  }

  return (
    <form onSubmit={(e) => { void handleSubmit(e); }} className="flex flex-col h-full">
      <div className="grid gap-4 flex-1">
        <div className="grid gap-2">
          <Label htmlFor="org-name">{t.dialogs.newOrganization.organizationName}</Label>
          <Input
            id="org-name"
            placeholder={t.dialogs.newOrganization.organizationNamePlaceholder}
            value={organizationName}
            onChange={(e) => { setOrganizationName(e.target.value); }}
            disabled={isFormDisabled}
            autoFocus
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="billing-country">{t.dialogs.newOrganization.billingCountry}</Label>
          <p className="text-sm text-muted-foreground">
            {t.dialogs.newOrganization.billingCountryDescription}
          </p>
          <CountryCombobox
            id="billing-country"
            value={selectedCountry}
            onChange={setSelectedCountry}
            disabled={isFormDisabled}
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="default-region">{t.organizationWizard.region.defaultRegion}</Label>
          <p className="text-sm text-muted-foreground">
            {t.organizationWizard.region.defaultRegionDescription}
          </p>
          <Select
            value={selectedRegion}
            onValueChange={setSelectedRegion}
            disabled={isFormDisabled}
          >
            <SelectTrigger id="default-region">
              <SelectValue placeholder={t.organizationWizard.region.noDefaultRegion} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">{t.organizationWizard.region.noDefaultRegion}</SelectItem>
              {availableRegions.map((region) => (
                <SelectItem key={region} value={region}>
                  {getRegionLabel(region)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className={`flex pt-4 ${onCancel ? 'justify-between' : 'justify-end'}`}>
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} disabled={isFormDisabled}>
            {cancelLabel ?? t.dialogs.newOrganization.cancel}
          </Button>
        )}
        <LoadingButton type="submit" disabled={!canSubmit} isLoading={isCreating}>
          {submitLabel ?? t.dialogs.newOrganization.create}
        </LoadingButton>
      </div>
    </form>
  )
}
